import React from "react";
import { Spinner, SpinnerSize } from "@fluentui/react";
import "./AiDraft.css";

/** The AI draft request's steps, in the order the backend runs them (AiDraftSubmitTask.Steps). */
export const DRAFT_STEPS = [
  { key: "key_images", label: "Collecting key images" },
  { key: "documents", label: "Collecting history documents" },
  { key: "render", label: "Converting key images to JPEG" },
  { key: "deidentify", label: "Removing the patient's name and ID from the clinical history, study details and file names" },
  { key: "send", label: "Sending to the AI service" },
  { key: "ai", label: "AI reading the images and history and writing the draft" },
];

/** The history summary's steps, in the order the backend runs them (AiHistorySummaryTask.Progress). */
export const SUMMARY_STEPS = [
  { key: "notes", label: "Reading clinical notes" },
  { key: "documents", label: "Collecting history documents" },
  { key: "deidentify", label: "Removing the patient's name and ID from notes, study details and file names" },
  { key: "ai", label: "AI reading the history and writing the summary" },
  { key: "saving", label: "Saving the summary" },
];

const elapsed = (iso, now) => {
  const started = Date.parse(iso || "");
  return Number.isNaN(started) ? null : Math.max(0, Math.floor((now - started) / 1000));
};

/**
 * Every step with its state: what the run recorded (done / active / failed, with its detail), and
 * "pending" for the steps it has not reached. Unknown keys from a newer backend are shown as they come.
 */
export const stepListFor = (definitions, recorded = []) => {
  const list = recorded || [];
  const byKey = Object.fromEntries(list.map((s) => [s.key, s]));
  const known = definitions.map((s) => ({ ...s, state: "pending", ...byKey[s.key] }));
  const extra = list.filter((s) => !definitions.some((d) => d.key === s.key)).map((s) => ({ label: s.key, ...s }));
  return [...known, ...extra];
};

const STEP_MARK = { done: "✓", failed: "✗", pending: "○" };

/**
 * Checklist of a background AI run (AI draft, history summary) as the backend records it:
 * ✓ done, spinner on the step in progress (with its seconds once it takes a while), ✗ where a
 * failed run stopped, ○ still to come.
 */
const ProgressSteps = ({ definitions, steps, now = Date.now(), testId = "ai-progress-steps" }) => (
  <ol className="ai-history-steps" data-testid={testId}>
    {stepListFor(definitions, steps).map((step) => {
      const seconds = step.state === "active" ? elapsed(step.at, now) : null;
      return (
        <li key={step.key} className={`ai-history-step ai-history-step--${step.state}`} data-state={step.state}>
          <span className="ai-history-step-mark" aria-hidden="true">
            {step.state === "active" ? <Spinner size={SpinnerSize.xSmall} /> : STEP_MARK[step.state] || "○"}
          </span>
          <span className="ai-history-step-text">
            {step.label}
            {step.detail && <span className="ai-history-step-detail"> · {step.detail}</span>}
            {seconds != null && seconds >= 3 && <span className="ai-history-step-detail"> · {seconds} s</span>}
          </span>
        </li>
      );
    })}
  </ol>
);

export default ProgressSteps;
